import React, { useEffect, forwardRef, useState, useRef, useMemo } from 'react';
import { createSelector } from 'reselect';
import { bytesToMegaBytes } from 'utils/formating';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import LaunchIcon from '@mui/icons-material/Launch';
import { getHighlightedText } from 'utils/highlightText';
import SortHeader from './SortHeader';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGripVertical,
  faGripHorizontal,
  faLock,
  faLockOpen,
  faPencilAlt,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import T from 'i18n';
import classnames from 'classnames';
import { withResizeDetector } from 'react-resize-detector';
import { useSelector } from 'react-redux';
import {
  selectCaseDataRoomSettings,
  selectCurrentCaseId,
  selectFetchDocumentsPending,
  selectShowExhibitNumber,
} from 'common/selectors';
import history from 'common/history';
import { PopoverStickOnHover } from 'features/common';
import TableInfo, { TableActions } from './TableInfo';
import {
  onCustomSortDate,
  onCustomSortNum,
  onCustomSortText,
  onCustomSortUploadedDate,
} from './utils';

import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useUpdateUserSettings } from 'features/common/redux/hooks';
import { complexValuesToOptions } from 'utils/arrays';
import { Tooltip } from '@mui/material';

const allColumnOptions = complexValuesToOptions(
  [
    { value: 'id', isFixed: false },
    { value: 'index', isFixed: true },
    { value: 'info', isFixed: true },
    { value: 'name', isFixed: true },
    { value: 'altDate', isFixed: false },
    { value: 'author', isFixed: false },
    { value: 'description', isFixed: false },
    { value: 'discoveryId', isFixed: false },
    { value: 'docDate', isFixed: false },
    { value: 'externalId', isFixed: false },
    { value: 'disclosureId', isFixed: false },
    { value: 'familyId', isFixed: false },
    { value: 'exhibitNumber', isFixed: false },
    { value: 'pageCount', isFixed: false },
    { value: 'recipient', isFixed: false },
    { value: 'size', isFixed: false },
    { value: 'classifyType', isFixed: false },
    { value: 'classifySubtype', isFixed: false },
    { value: 'type', isFixed: false },
    { value: 'dateUploaded', isFixed: false },
  ],
  'generic',
);

const allColumnOptionsSmallView = complexValuesToOptions(
  [
    { value: 'id', isFixed: false },
    { value: 'index', isFixed: true },
    { value: 'info', isFixed: true },
    { value: 'name', isFixed: true },
    { value: 'altDate', isFixed: false },
    { value: 'author', isFixed: false },
    { value: 'description', isFixed: false },
    { value: 'discoveryId', isFixed: false },
    { value: 'docDate', isFixed: false },
    { value: 'externalId', isFixed: false },
    { value: 'disclosureId', isFixed: false },
    { value: 'familyId', isFixed: false },
    { value: 'exhibitNumber', isFixed: false },
    { value: 'pageCount', isFixed: false },
    { value: 'recipient', isFixed: false },
    { value: 'size', isFixed: false },
    { value: 'classifyType', isFixed: false },
    { value: 'classifySubtype', isFixed: false },
    { value: 'type', isFixed: false },
    { value: 'dateUploaded', isFixed: false },
  ],
  'generic',
);

const DEFAULT_COLUMNS = ['index', 'name', 'info'];
const DEFAULT_SMALL_COLUMNS = ['index', 'name', 'info'];
const normalizeWithDefaults = (columns: string[] | null | undefined, defaults: string[]) =>
  Array.from(new Set([...(Array.isArray(columns) ? columns : []), ...defaults]));

function ColumnHeaderMenu({ columnId, children, sortMode, isSmallView }: any) {
  const { userSettings, updateUserSettings } = useUpdateUserSettings();
  const safeUserSettings = userSettings || {};
  const caseDataRoomSettings = useSelector(selectCaseDataRoomSettings);
  const showExhibitNumber = useSelector(selectShowExhibitNumber);
  const caseId = useSelector(selectCurrentCaseId);
  // Columns that are always present if none are configured
  const defaultColumns = DEFAULT_COLUMNS;
  const defaultSmallColumns = DEFAULT_SMALL_COLUMNS; // same mandatory set for small view

  const userDataRoom = safeUserSettings.dataRoom || {};

  const baseColumns = useMemo(() => {
    const selectedByCase = caseId ? userDataRoom.selectedColumns?.[caseId] : null;
    const caseColumns =
      caseDataRoomSettings?.columns && caseDataRoomSettings.columns.length
        ? caseDataRoomSettings.columns
        : null;
    return normalizeWithDefaults(selectedByCase || caseColumns || defaultColumns, defaultColumns);
  }, [caseId, userDataRoom.selectedColumns, caseDataRoomSettings?.columns, defaultColumns]);

  const baseSmallColumns = useMemo(() => {
    const selectedByCase = caseId ? userDataRoom.selectedColumnsSmallView?.[caseId] : null;
    if (selectedByCase?.length) return [...selectedByCase];
    if (caseDataRoomSettings?.columnsSmallView?.length)
      return [...caseDataRoomSettings.columnsSmallView];
    return [...defaultSmallColumns];
  }, [
    caseId,
    userDataRoom.selectedColumnsSmallView,
    caseDataRoomSettings?.columnsSmallView,
    defaultSmallColumns,
  ]);
  const availableColumnOptions = useMemo(
    () =>
      showExhibitNumber
        ? allColumnOptions
        : allColumnOptions.filter((opt: any) => opt.value !== 'exhibitNumber'),
    [showExhibitNumber],
  );
  const availableSmallColumnOptions = useMemo(
    () =>
      showExhibitNumber
        ? allColumnOptionsSmallView
        : allColumnOptionsSmallView.filter((opt: any) => opt.value !== 'exhibitNumber'),
    [showExhibitNumber],
  );
  const sortedAvailableColumnOptions = useMemo(
    () =>
      [...availableColumnOptions].sort((a: any, b: any) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      ),
    [availableColumnOptions],
  );
  const sortedAvailableSmallColumnOptions = useMemo(
    () =>
      [...availableSmallColumnOptions].sort((a: any, b: any) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      ),
    [availableSmallColumnOptions],
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [hover, setHover] = useState(false);
  const [skipCommitOnClose, setSkipCommitOnClose] = useState(false);
  const [pendingCols, setPendingCols] = useState<string[]>(baseColumns);
  const [pendingSmallCols, setPendingSmallCols] = useState<string[]>(baseSmallColumns);

  useEffect(() => {
    setPendingCols(baseColumns);
  }, [baseColumns]);

  useEffect(() => {
    setPendingSmallCols(baseSmallColumns);
  }, [baseSmallColumns]);

  const areArraysEqual = (a: string[] = [], b: string[] = []) =>
    a.length === b.length && a.every((val, idx) => val === b[idx]);

  const persistColumns = (nextColumns: string[], nextSmallColumns: string[]) => {
    const normalizedColumns = normalizeWithDefaults(nextColumns, defaultColumns);
    const normalizedSmallColumns = [...nextSmallColumns];
    const selectedColumns = caseId
      ? {
          ...(userDataRoom.selectedColumns || {}),
          [caseId]: normalizedColumns,
        }
      : userDataRoom.selectedColumns;
    const selectedSmallColumns = caseId
      ? {
          ...(userDataRoom.selectedColumnsSmallView || {}),
          [caseId]: normalizedSmallColumns,
        }
      : userDataRoom.selectedColumnsSmallView;
    updateUserSettings({
      userSettings: {
        ...safeUserSettings,
        dataRoom: {
          ...userDataRoom,
          selectedColumns,
          selectedColumnsSmallView: selectedSmallColumns,
        },
      },
    });
  };

  const open = Boolean(anchorEl);

  const toggleSmallColumn = (value: string) => {
    const next = pendingSmallCols.includes(value)
      ? pendingSmallCols.filter((c: any) => c !== value)
      : [...pendingSmallCols, value];
    setPendingSmallCols(next);
  };

  const handleClick = (e: any) => {
    e.stopPropagation();
    // Reset pending to current saved settings and clear skip flag
    setPendingCols(baseColumns);
    setPendingSmallCols(baseSmallColumns);
    setSkipCommitOnClose(false);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: React.MouseEvent<Element, MouseEvent> | React.SyntheticEvent) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
    if (skipCommitOnClose) {
      setSkipCommitOnClose(false);
      return;
    }
    const normalizedPending = normalizeWithDefaults(pendingCols, defaultColumns);
    const sameCols = areArraysEqual(normalizedPending, baseColumns);
    const sameSmall = areArraysEqual(pendingSmallCols, baseSmallColumns);
    if (sameCols && sameSmall) {
      return;
    }
    persistColumns(pendingCols, pendingSmallCols);
  };

  const hideColumn = () => {
    const initial = pendingCols || baseColumns;
    const updated = initial.filter((col: any) => col !== columnId);
    setPendingCols(updated);
    persistColumns(updated, pendingSmallCols);
    // Prevent the close handler from committing again
    setSkipCommitOnClose(true);
    setAnchorEl(null);
  };

  const toggleColumn = (value: any) => {
    setPendingCols((current: any) =>
      current.includes(value) ? current.filter((c: any) => c !== value) : [...current, value],
    );
  };
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {!sortMode && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (open) {
              handleClose(e);
            } else {
              handleClick(e);
            }
          }}
          onMouseEnter={(e) => {
            e.stopPropagation();
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
          }}
          style={{
            visibility: open || hover ? 'visible' : 'hidden',
            marginLeft: '4px',
            zIndex: 1000,
            padding: '4px',
          }}
        >
          <MoreVertIcon fontSize="medium" />
        </IconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={(e: any) => {
          e.stopPropagation();
          handleClose(e);
        }}
        PaperProps={{
          style: { maxHeight: 300, width: 300 },
          onClick: (e: any) => e.stopPropagation(),
        }}
      >
        {(!isSmallView
          ? [
              !['name', 'index', 'tab', 'info'].includes(columnId) ? (
                <MenuItem key="hide" onClick={hideColumn}>
                  {T.translate('generic.hideColumn')}
                </MenuItem>
              ) : null,
              <MenuItem key="manage" disabled>
                {T.translate('generic.manageColumns')}
              </MenuItem>,
              ...sortedAvailableColumnOptions.map((opt: any) => (
                <MenuItem
                  key={opt.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (opt.isFixed) return;
                    toggleColumn(opt.value);
                  }}
                  disableRipple
                  disabled={opt.isFixed}
                >
                  <Checkbox
                    checked={pendingCols.includes(opt.value)}
                    sx={{ '&.Mui-checked': { color: '#748CEC' } }}
                    disabled={opt.isFixed}
                  />
                  {opt.label}
                </MenuItem>
              )),
            ]
          : sortedAvailableSmallColumnOptions.map((opt: any) => (
              <MenuItem
                key={opt.value}
                onClick={(e) => {
                  e.stopPropagation();
                  if (opt.isFixed) return;
                  toggleSmallColumn(opt.value);
                }}
                disableRipple
                disabled={opt.isFixed}
              >
                <Checkbox
                  checked={pendingSmallCols.includes(opt.value)}
                  sx={{ '&.Mui-checked': { color: '#748CEC' } }}
                  disabled={opt.isFixed}
                />
                {opt.label}
              </MenuItem>
            ))
        ).filter(Boolean)}
      </Menu>
    </div>
  );
}

const mapCustomColumnsToNewValues = (columns: any) => {
  const getProperName = (col: string) => {
    switch (col) {
      case 'desc':
        return 'description';
      case 'date':
        return 'docDate';
      case 'discovery':
        return 'discoveryId';
      case 'fileType':
        return 'type';
      case 'pagesCount':
        return 'pageCount';
      case 'fileAuthor':
        return 'author';
      case 'fileRecipient':
        return 'recipient';
      case 'parentId':
        return 'disclosureId';
      default:
        return col;
    }
  };

  columns.forEach((col: any, index: number) => {
    columns[index] = getProperName(col);
  });

  return columns;
};

export default createSelector(
  (
    file: any,
    selectedColumns: any,
    selectedColumnsSmallView: any,
    { folders: { canSortDocuments = undefined } = {} }: any,
    sortMode: any,
    setSortMode: any,
    editMode: any,
    setEditMode: any,
    updateDocumentSort: any,
    idMinWidth: any,
    isTrialBookAgreed: any,
    isReadOnlyTrialbook: any,
    isQuickLinksPage: any,
    showGoToPageFilter: any,
    present: any,
    filter: any,
    currentFolder: any,
    isWiderDateColumn: any,
    isWiderColumns: any,
    isPresentModePage: any,
    isPresentModeWithBackEnd: any,
    isTagsFolder: any,
    setDocSearchVal: any,
    tableRef: any,
    setFile: any,
    canPresentToHearingRoom: boolean,
    isSearchModePage: boolean,
    isBundleTabPage: boolean,
    showDocFinderTab: boolean,
    handleShow: any,
    getFolderSpecificFileDetails: any,
    onRowSelect: any,
    onOpenViewDrawer: any,
    showExhibitNumber = false,
  ) => ({
    file,
    selectedColumns,
    selectedColumnsSmallView,
    canSortDocuments,
    sortMode,
    setSortMode,
    updateDocumentSort,
    idMinWidth,
    isTrialBookAgreed,
    isReadOnlyTrialbook,
    isQuickLinksPage,
    showGoToPageFilter,
    present,
    filter,
    currentFolder,
    isWiderDateColumn,
    isWiderColumns,
    isPresentModePage,
    isPresentModeWithBackEnd,
    isTagsFolder,
    setDocSearchVal,
    tableRef,
    setFile,
    canPresentToHearingRoom,
    editMode,
    setEditMode,
    isSearchModePage,
    isBundleTabPage,
    showDocFinderTab,
    handleShow,
    getFolderSpecificFileDetails,
    onRowSelect,
    onOpenViewDrawer,
    showExhibitNumber,
  }),
  ({
    file,
    selectedColumns,
    selectedColumnsSmallView,
    canSortDocuments,
    sortMode,
    setSortMode,
    updateDocumentSort,
    idMinWidth,
    isTrialBookAgreed,
    isReadOnlyTrialbook,
    isQuickLinksPage,
    showGoToPageFilter,
    present,
    filter,
    currentFolder,
    isWiderDateColumn,
    isWiderColumns,
    isPresentModePage,
    isPresentModeWithBackEnd,
    isTagsFolder,
    setDocSearchVal,
    tableRef,
    setFile,
    canPresentToHearingRoom,
    editMode,
    isSearchModePage,
    isBundleTabPage,
    showDocFinderTab,
    handleShow,
    getFolderSpecificFileDetails,
    onRowSelect,
    onOpenViewDrawer,
    showExhibitNumber,
  }: any) => {
    const columnsWidth = {
      index: 4,
      tab: 7,
      id: 10,
      description: 20,
      globalPages: 12,
      externalId: 12,
      exhibitNumber: 12,
      discoveryId: 12,
      author: isWiderColumns ? 15 : 10,
      recipient: isWiderColumns ? 15 : 12,
      viewed: 12,
      dateViewed: 12,
      docDate: isWiderDateColumn ? 12 : 12,
      altDate: 12,
      disclosureId: 12,
      familyId: 12,
      dateUploaded: 10,
      pageCount: 5,
      classifyType: 12,
      classifySubtype: 18,
      type: 5,
      size: 11,
      info: 15,
      actions: 3,
    } as any;
    const restrictedColumnsInQuickLinksPage = [
      'externalId',
      'exhibitNumber',
      'discoveryId',
      'docDate',
      'altDate',
      'disclosureId',
      'familyId',
      'dateUploaded',
      'description',
      'author',
      'recipient',
    ];
    let customColumns = (!file ? selectedColumns : selectedColumnsSmallView) as any;
    if (customColumns) {
      if (isTrialBookAgreed) customColumns[0] = 'tab';
      else customColumns[0] = 'index';
      if (showGoToPageFilter || isPresentModePage || showDocFinderTab)
        if (customColumns.includes('index')) customColumns.push('tab');
        else customColumns.push('index');
      if (isQuickLinksPage && isTagsFolder) {
        customColumns = customColumns.filter(
          (col: any) => !restrictedColumnsInQuickLinksPage.includes(col),
        );
      }
    }

    // needed to keep backwards compatibility with case and user settings for custom columns
    mapCustomColumnsToNewValues(customColumns);
    if (customColumns && !showExhibitNumber) {
      customColumns = customColumns.filter((col: string) => col !== 'exhibitNumber');
    }

    const defaultColumns = [
      (isTrialBookAgreed || showGoToPageFilter || isPresentModePage || showDocFinderTab) && 'tab',
      'id',
      'name',
      !isBundleTabPage && (showGoToPageFilter || isPresentModePage) && 'globalPages',
      isQuickLinksPage && isTagsFolder && 'viewed',
      'info',
    ].filter(Boolean);

    const getNameColumnWidth = (columns: any) => {
      return columns
        .filter((col: any) => col !== 'name' && !(isBundleTabPage && col === 'globalPages'))
        .reduce((acc: any, col: any) => acc + (columnsWidth[col] || 0), 0);
    };
    let columns = customColumns ? customColumns : defaultColumns;
    if (!showExhibitNumber) {
      columns = columns.filter((col: string) => col !== 'exhibitNumber');
    }
    if (editMode) {
      const editableColumnIds = [
        'index',
        'tab',
        'docDate',
        'name',
        'author',
        'discoveryId',
        'externalId',
        'exhibitNumber',
        'info',
        'id',
      ];
      columns = columns.filter((col: string) => editableColumnIds.includes(col));
    }
    if (!columns.includes('actions')) {
      columns = [...columns, 'actions'];
    }
    const getBundleLocationGlobalPrefix = (row: any) => {
      return (
        row.bundleLocations &&
        row.bundleLocations.length > 0 &&
        row.bundleLocations[0].globalPagePrefix
      );
    };
    const getBundleLocationTab = (row: any) => {
      return row.bundleLocations && row.bundleLocations.length > 0 && row.bundleLocations[0].tab;
    };
    return [
      {
        id: 'index',
        Header:
          canSortDocuments && canSortDocuments() && !isReadOnlyTrialbook
            ? () => {
                const icon = sortMode ? faLockOpen : faLock;
                // When locked (faLock), use only MUI Tooltip (no react-bootstrap OverlayTrigger) to avoid nested overlay conflicts.
                if (icon === faLock) {
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip
                        title={
                          T.translate('case.unlockToReorder') ||
                          T.translate('unlockToReorder') ||
                          ' '
                        }
                        placement="top"
                        arrow
                        enterDelay={0}
                        enterNextDelay={0}
                      >
                        <span style={{ display: 'inline-flex' }}>
                          <FontAwesomeIcon
                            style={{
                              marginBottom: '2px',
                              width: '100%',
                              minWidth: '10px',
                              ...(isTrialBookAgreed && { color: '#d57200b8' }),
                            }}
                            icon={icon}
                          />
                        </span>
                      </Tooltip>
                    </div>
                  );
                }
                // When unlocked / in sort mode use original OverlayTrigger popover behavior
                return (
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 500, hide: 400 }}
                    overlay={
                      <Popover id="index">
                        <Popover.Content>
                          {sortMode
                            ? T.translate('case.exitSortMode')
                            : isTrialBookAgreed
                              ? T.translate('case.enterSortModePartial')
                              : T.translate('case.enterSortMode')}
                        </Popover.Content>
                      </Popover>
                    }
                  >
                    <>
                      <FontAwesomeIcon
                        style={{
                          marginBottom: '2px',
                          width: '100%',
                          minWidth: '10px',
                          ...(isTrialBookAgreed && { color: '#d57200b8' }),
                        }}
                        icon={icon}
                      />
                    </>
                  </OverlayTrigger>
                );
              }
            : null,
        sort: false,
        onClick:
          canSortDocuments &&
          canSortDocuments() &&
          !isReadOnlyTrialbook &&
          (({ setSortByColumn, setSortDirection, onSort, event }: any) => {
            setSortMode(!sortMode);
            setSortByColumn(null);
            setSortDirection(null);
            onSort({
              sortByColumn: null,
              sortDirection: null,
              event,
            });

            // unselect all rows when clicked - per request TV-380 as only possible solution atm
            tableRef.current?.unselectAllRows();
            event.stopPropagation();
          }),
        Cell: forwardRef(
          (
            {
              dragHandleProps,
              sortMode: sortModeInternal,
              isScrolling,
              rowIndex,
              rowHover,
              rowSelected,
              ...row
            }: any,
            ref,
          ) => {
            const icon = sortModeInternal ? faGripHorizontal : faGripVertical;
            const dragOnlyLateInsertRow = isTrialBookAgreed && !row.agreed;
            const dragBundleRow = !isTrialBookAgreed || dragOnlyLateInsertRow;
            const showHandle =
              (rowHover || rowSelected) &&
              (!sortModeInternal || (sortModeInternal && dragBundleRow));
            return (
              <div
                style={{
                  color: '#9A9A9A',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  height: '20px',
                }}
              >
                <div
                  {...dragHandleProps}
                  className={classnames('hover-change', {
                    draggable: !sortModeInternal || (sortModeInternal && dragBundleRow),
                  })}
                  ref={ref}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    marginBottom: '2px',
                    minWidth: '10px',
                    width: '10px',
                  }}
                >
                  <span className={classnames({ new: !showHandle, old: showHandle })}>
                    {isScrolling ? rowIndex + 1 : row.sortOrder}
                  </span>
                  <FontAwesomeIcon
                    className={classnames({ new: showHandle, old: !showHandle })}
                    icon={icon}
                  />
                </div>
              </div>
            );
          },
        ),
        forwardRef: true,
        minWidth: '50px',
        width: `${columnsWidth.index}rem`,
      },
      {
        Header: T.translate('case.tab'),
        id: 'tab',
        accessor: 'tab',
        minWidth: '80px',
        width: `${columnsWidth.tab}rem`,
        textAlign: 'center',
        Cell: (row: any) => {
          const tabValue =
            !row.tab && showDocFinderTab ? getBundleLocationTab(row) || row.tab : row.tab;

          return !tabValue ? (
            <OverlayTrigger
              placement="right"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Popover id="tabPopover" style={{ maxWidth: '350px', fontSize: '0.75rem' }}>
                  <React.Fragment>
                    <Popover.Content>{T.translate('case.noTabValMsg')}</Popover.Content>
                  </React.Fragment>
                </Popover>
              }
            >
              <FontAwesomeIcon style={{ color: '#ff9007' }} icon={faPencilAlt} />
            </OverlayTrigger>
          ) : (
            <div
              style={{
                ...(isTrialBookAgreed && !row.agreed && { color: '#d57200b8', fontWeight: 600 }),
              }}
            >
              {isBundleTabPage && (row.globalPagePrefix || getBundleLocationGlobalPrefix(row))
                ? `${row.globalPagePrefix || getBundleLocationGlobalPrefix(row)} / ${tabValue}`
                : tabValue}
            </div>
          );
        },
        ...(sortMode && { sort: false }),
      },
      {
        id: 'id',
        Header: () => (
          <SortHeader
            text="case.documentId"
            onSortDownClick={() => updateDocumentSort('id', 'asc')}
            onSortUpClick={() => updateDocumentSort('id', 'desc')}
            sortMode={sortMode}
            columnId="id"
          />
        ),
        Cell: (row: any) => {
          return (
            <div
              dangerouslySetInnerHTML={{
                __html: getHighlightedText(row.id, filter.term),
              }}
            ></div>
          );
        },
        minWidth: idMinWidth + 20, //adding 20 px for doc re-sequance(lock) issue
        width: `${columnsWidth.id}rem`,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => (
          <SortHeader
            text="case.name"
            onSortDownClick={() => updateDocumentSort('name', 'asc')}
            onSortUpClick={() => updateDocumentSort('name', 'desc')}
            sortMode={sortMode}
            columnId="name"
          />
        ),
        id: 'name',
        minWidth: '200px',
        width: editMode ? '100%' : `calc(100% - ${getNameColumnWidth(columns)}rem)`,
        Cell: withResizeDetector(({ width, height, ...row }: any) => {
          const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
          const [goToPage, setGoTOPage] = useState(0);
          const isFetchDocumentsPending = useSelector(selectFetchDocumentsPending);
          const startPage = row.startPage && row.startPage.split('.');

          // Define lpSearch to handle formats with slashes (including complex prefixes like G4.0/3/6, A99.99/5/6, and F1/5.1/5)
          const lpSearch =
            filter.term && // Simple ID/page formats like "1722/6"
            (/^[0-9]{1,6}[/][0-9]{1,7}?$/.test(filter.term) ||
              // Ticket formats with slashes like "TV-426-9885/4"
              /^[A-Za-z]+-\d{2,3}-\d{4,}\/\d{1,4}$/.test(filter.term) ||
              // Shorter ticket formats like "TV-426/9"
              /^[A-Za-z]+-\d{2,}\/\d{1,4}$/.test(filter.term) ||
              // Numeric ticket formats like "426-3761/9"
              /^\d+-\d+\/\d{1,4}$/.test(filter.term) ||
              // Global prefix formats with TWO slashes like "AA/5/8", "G4.0/3/6", "A99.99/5/6", or "F1/5.1/5"
              /^[A-Za-z][A-Za-z0-9]*(\.\d{1,2})?\/\d+(\.\d+)?\/\d+$/.test(filter.term));

          // Define gpSearch specifically for formats like "AA3", "A9.99", "A99.99", etc. (direct global prefix without slashes)
          const gpSearch =
            !columns.includes('globalPages') &&
            startPage &&
            !row.matches &&
            filter.term &&
            row.globalPagePrefix &&
            /^[a-zA-Z][A-Za-z0-9]*(\.\d{1,2})?$/.test(filter.term) && // Updated to handle A9.99, A99.99, etc.
            filter.term.toLowerCase().startsWith(row.globalPagePrefix.toLowerCase()) &&
            // Ensure no slashes in the filter term
            !filter.term.includes('/') &&
            filter.term.length > row.globalPagePrefix.length;

          // Update how we extract the page number based on format
          const localPage =
            lpSearch && filter.term
              ? filter.term.includes('-') &&
                (/^[A-Za-z]+-\d{2,3}-\d{4,}\/\d{1,4}$/.test(filter.term) ||
                  /^[A-Za-z]+-\d{2,}\/\d{1,4}$/.test(filter.term) ||
                  /^\d+-\d+\/\d{1,4}$/.test(filter.term))
                ? filter.term.split('/')[1] // For patterns like "TV-426-9885/4"
                : /^[A-Za-z][A-Za-z0-9]*(\.\d{1,2})?\/\d+(\.\d+)?\/\d+$/.test(filter.term)
                  ? filter.term.split('/')[2] // For global prefix with TWO slashes: "AA/5/8" or "F1/5.1/5"
                  : filter.term.split('/')[1] // For other patterns with ONE slash: "1722/6" or "F1/5.1"
              : 1;

          // Calculate searchIdx based on the pattern type
          const searchIdx =
            (gpSearch && filter.term.substring(row.globalPagePrefix.length)) || // For "AA3" format, extract just the number
            (lpSearch && localPage) ||
            '';

          const measuredRef = useRef<HTMLDivElement>(null);

          useEffect(() => {
            setShouldShowTooltip(
              measuredRef.current !== null &&
                measuredRef.current.offsetWidth < measuredRef.current.scrollWidth,
            );
          }, [width, height]);

          useEffect(() => {
            if (gpSearch) {
              if (searchIdx.includes('.') && startPage.length > 1) {
                const filterVal = searchIdx.split('.');
                setGoTOPage(parseInt(filterVal[1], 10) - parseInt(startPage[1], 10) + 1);
              } else {
                setGoTOPage(parseInt(searchIdx, 10) - parseInt(row.startPage) + 1);
              }
            } else if (lpSearch) setGoTOPage(parseInt(searchIdx, 10));
          }, [row, startPage, searchIdx, gpSearch, lpSearch]);

          return (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 500, hide: 400 }}
                  trigger={shouldShowTooltip ? ['hover', 'focus'] : []}
                  overlay={
                    <Popover id="name" style={{ pointerEvents: 'none' }}>
                      <Popover.Content>{row.name || row.originalName}</Popover.Content>
                    </Popover>
                  }
                >
                  <div
                    ref={measuredRef}
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedText(row.name || row.originalName, filter.term),
                    }}
                  ></div>
                </OverlayTrigger>
                {((gpSearch && !isPresentModePage) || (lpSearch && !isSearchModePage)) &&
                  !isFetchDocumentsPending && (
                    <Chip
                      color="primary"
                      icon={<LaunchIcon />}
                      style={{
                        height: 'auto',
                        flexFlow: 'row',
                        background: '#7BA0F4',
                      }}
                      label={
                        gpSearch ? (!file ? `Page ${filter.term}` : 'Gp') : `Page ${searchIdx}`
                      }
                      onClick={() => {
                        history.push(
                          history.location.pathname.replace(/\/files.*/, ``) +
                            `/files/${row.id}${
                              isPresentModePage ? `/compositeKey/${row.compositeKey}` : ``
                            }${
                              canPresentToHearingRoom &&
                              (!!present || isPresentModePage || isPresentModeWithBackEnd)
                                ? `?goToPage=${goToPage}&present=${
                                    isPresentModePage || isPresentModeWithBackEnd
                                      ? 'public'
                                      : present
                                  }`
                                : `?goToPage=${goToPage}`
                            }`,
                        );
                        setFile(row);
                      }}
                    ></Chip>
                  )}
                {row.matches && row.matches.length > 0 && (
                  <PopoverStickOnHover
                    component={
                      <Popover.Content>
                        <PerfectScrollbar
                          options={{ suppressScrollX: true }}
                          style={{ maxHeight: '80vh' }}
                        >
                          {row.matches.map((highlight: any, index: number) => {
                            function handleClick() {
                              const selectedText = getSelection()?.toString();
                              if (selectedText) return;
                              const parentRow = document.getElementById(row.compositeKey || row.id);
                              parentRow?.click();
                              history.push(
                                history.location.pathname.replace(/\/files.*/, ``) +
                                  `/files/${row.id}`,
                              );
                              setDocSearchVal(highlight.text, highlight.keyPhrase, row.id);
                              setFile(row);
                            }
                            const strWithoutPhrase =
                              highlight.keyPhrase &&
                              highlight.text.split(new RegExp(highlight.keyPhrase, 'i'));
                            return (
                              <div
                                key={index}
                                className="matchesItem"
                                style={{
                                  ...(index % 2 !== 0 && { backgroundColor: '#eff0f4' }),
                                }}
                                onClick={handleClick}
                              >
                                {strWithoutPhrase && highlight.keyPhrase ? (
                                  <>
                                    {strWithoutPhrase[0]}
                                    {<b>{highlight.keyPhrase}</b>}
                                    {strWithoutPhrase[1]}
                                  </>
                                ) : (
                                  highlight.text
                                )}
                              </div>
                            );
                          })}
                        </PerfectScrollbar>
                      </Popover.Content>
                    }
                    placement="left"
                    onMouseEnter={() => {}}
                  >
                    <Chip
                      color="primary"
                      icon={<SearchIcon style={{ marginLeft: '0.5rem' }} />}
                      style={{
                        height: 'auto',
                        background: '#7BA0F4',
                      }}
                      label={Math.min(99, row.matches.length)}
                      onClick={() => {
                        history.push(
                          history.location.pathname.replace(/\/files.*/, ``) + `/files/${row.id}`,
                        );
                        setDocSearchVal(filter.term.replace(/^"(.*)"$/, '$1'));
                        setFile(row);
                      }}
                    ></Chip>
                  </PopoverStickOnHover>
                )}
              </div>
            </div>
          );
        }),
        accessor: (row: any) => row.name || row.originalName,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => (
          <SortHeader
            text="case.description"
            onSortDownClick={() => updateDocumentSort('description', 'asc', onCustomSortText)}
            onSortUpClick={() => updateDocumentSort('description', 'desc', onCustomSortText)}
            sortMode={sortMode}
            columnId="description"
          />
        ),
        id: 'description',
        minWidth: '210px',
        width: `${columnsWidth.description}rem`,
        Cell: withResizeDetector(({ width, height, ...row }: any) => {
          const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

          const measuredDescRef = useRef<HTMLDivElement>(null);

          useEffect(() => {
            setShouldShowTooltip(
              measuredDescRef.current !== null &&
                measuredDescRef.current.offsetWidth < measuredDescRef.current.scrollWidth,
            );
          }, [width, height]);

          return (
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%' }}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 500, hide: 400 }}
                trigger={shouldShowTooltip ? ['hover', 'focus'] : []}
                overlay={
                  <Popover id="description" style={{ pointerEvents: 'none' }}>
                    <Popover.Content>{row.description}</Popover.Content>
                  </Popover>
                }
              >
                <div
                  ref={measuredDescRef}
                  style={{
                    width: `100%`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedText(row.description || '', filter.term),
                  }}
                ></div>
              </OverlayTrigger>
            </div>
          );
        }),
        accessor: (row: any) => row.description,
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      !isBundleTabPage && {
        id: 'globalPages',
        Header: () => (
          <SortHeader
            text="case.globalPages"
            onSortDownClick={() => updateDocumentSort('startPage', 'asc', onCustomSortNum)}
            onSortUpClick={() => updateDocumentSort('startPage', 'desc', onCustomSortNum)}
            sortMode={false}
            columnId="startPage"
          />
        ),
        minWidth: '180px',
        width: `${columnsWidth.globalPages}rem`,
        Cell: (row: any) => {
          const [goToPage, setGoTOPage] = useState(0);
          // const { file: fileId, compositeKey } = useSelector(selectParams) as any;
          // const isCurrentFile = row.compositeKey === compositeKey || row.id === fileId;
          const startPage = row.startPage && row.startPage.split('.');
          // Replace the current isGPFilter condition (around line 717-721) with this:
          const isGPFilter =
            startPage &&
            (filter.goToGlobalPageIdx ||
              (filter.term &&
                filter.term.toLowerCase().startsWith(row.globalPagePrefix.toLowerCase()) &&
                /^[a-zA-Z][A-Za-z0-9]*(\.\d{1,2})?$/.test(filter.term) &&
                // These additional conditions ensure there's a page number after the prefix
                filter.term.length > row.globalPagePrefix.length &&
                // Ensure filter term is not exactly the prefix
                filter.term.toLowerCase() !== row.globalPagePrefix.toLowerCase() &&
                // Make sure what comes after is a valid number
                !isNaN(
                  Number(filter.term.toLowerCase().replace(row.globalPagePrefix.toLowerCase(), '')),
                )));

          const filterTerm =
            (filter.goToGlobalPageIdx
              ? filter.goToGlobalPageIdx
              : filter.term &&
                row &&
                filter.term.toLowerCase().replace(row.globalPagePrefix.toLowerCase(), '')) || '';
          useEffect(() => {
            if (isGPFilter)
              if (filterTerm.includes('.') && startPage.length > 1) {
                const filterVal = filterTerm.split('.');
                setGoTOPage(parseInt(filterVal[1], 10) - parseInt(startPage[1], 10) + 1);
              } else {
                setGoTOPage(parseInt(filterTerm, 10) - parseInt(row.startPage) + 1);
              }
          }, [row, startPage, isGPFilter, filterTerm]);
          return isGPFilter /*&& (goToPage !== currentOpenPage ||!isCurrentFile) */ ? (
            <Chip
              color="primary"
              icon={<LaunchIcon />}
              style={{
                height: 'auto',
                background: '#7BA0F4',
              }}
              label={'Page ' + row.globalPagePrefix + filterTerm}
              onClick={() => {
                history.push(
                  history.location.pathname.replace(/\/files.*/, ``) +
                    `/files/${row.id}${
                      isPresentModePage ? `/compositeKey/${row.compositeKey}` : ``
                    }${
                      !!present || isPresentModePage
                        ? `?${filterTerm ? `goToPage=${goToPage}` : ''}&present=${isPresentModePage ? 'public' : present}`
                        : filterTerm
                          ? `?goToPage=${goToPage}`
                          : ''
                    }`,
                );
                setFile(row);
              }}
            ></Chip>
          ) : (
            `${row.globalPagePrefix}${row.startPage} - ${row.globalPagePrefix}${row.lastPage}`
          );
        },
        onCustomSort: onCustomSortNum,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => (
          <SortHeader
            text={'case.externalId'}
            onSortDownClick={() => updateDocumentSort('externalId', 'asc')}
            onSortUpClick={() => updateDocumentSort('externalId', 'desc')}
            sortMode={sortMode}
            columnId="externalId"
          />
        ),
        id: 'externalId',
        minWidth: '150px',
        width: `${columnsWidth.externalId}rem`,
        accessor: (row: any) => (
          <OverlayTrigger
            placement="top"
            delay={{ show: 500, hide: 400 }}
            trigger={row.externalId && row.externalId.length > 18 && ['hover', 'focus']}
            overlay={
              <Popover id="externalId" style={{ pointerEvents: 'none' }}>
                <Popover.Content>{row.externalId}</Popover.Content>
              </Popover>
            }
          >
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
              }}
            >
              {row.externalId}
            </div>
          </OverlayTrigger>
        ),
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => (
          <SortHeader
            text={'case.exhibitNumber'}
            onSortDownClick={() => updateDocumentSort('exhibitNumber', 'asc', onCustomSortText)}
            onSortUpClick={() => updateDocumentSort('exhibitNumber', 'desc', onCustomSortText)}
            sortMode={sortMode}
            columnId="exhibitNumber"
          />
        ),
        id: 'exhibitNumber',
        minWidth: '180px',
        width: `${columnsWidth.exhibitNumber}rem`,
        accessor: (row: any) => (
          <OverlayTrigger
            placement="top"
            delay={{ show: 500, hide: 400 }}
            trigger={row.exhibitNumber && row.exhibitNumber.length > 18 && ['hover', 'focus']}
            overlay={
              <Popover id="exhibitNumber" style={{ pointerEvents: 'none' }}>
                <Popover.Content>{row.exhibitNumber}</Popover.Content>
              </Popover>
            }
          >
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
              }}
            >
              {row.exhibitNumber}
            </div>
          </OverlayTrigger>
        ),
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => (
          <SortHeader
            text={'case.discoveryId'}
            onSortDownClick={() => updateDocumentSort('discoveryId', 'asc')}
            onSortUpClick={() => updateDocumentSort('discoveryId', 'desc')}
            sortMode={sortMode}
            columnId="discoveryId"
          />
        ),
        id: 'discoveryId',
        minWidth: '150px',
        width: `${columnsWidth.discoveryId}rem`,
        accessor: (row: any) => (
          <OverlayTrigger
            placement="top"
            delay={{ show: 500, hide: 400 }}
            trigger={row.discoveryId && row.discoveryId.length > 18 && ['hover', 'focus']}
            overlay={
              <Popover id="discoveryId" style={{ pointerEvents: 'none' }}>
                <Popover.Content>{row.discoveryId}</Popover.Content>
              </Popover>
            }
          >
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
              }}
              dangerouslySetInnerHTML={{
                __html: getHighlightedText(row.discoveryId || '', filter.term),
              }}
            ></div>
          </OverlayTrigger>
        ),
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'familyId',
        Header: () => (
          <SortHeader
            text="case.familyId"
            onSortDownClick={() => updateDocumentSort('familyId', 'asc')}
            onSortUpClick={() => updateDocumentSort('familyId', 'desc')}
            sortMode={sortMode}
            columnId="familyId"
          />
        ),
        minWidth: '150px',
        width: `${columnsWidth.familyId}rem`,
        Cell: withResizeDetector(({ width, height, ...row }: any) => {
          const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
          const familyIdValue =
            row.familyId !== null && row.familyId !== undefined ? String(row.familyId) : '';

          const measuredFamilyIdRef = useRef<HTMLDivElement>(null);

          useEffect(() => {
            setShouldShowTooltip(
              measuredFamilyIdRef.current !== null &&
                measuredFamilyIdRef.current.offsetWidth < measuredFamilyIdRef.current.scrollWidth,
            );
          }, [width, height]);

          return (
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%' }}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 500, hide: 400 }}
                trigger={shouldShowTooltip ? ['hover', 'focus'] : []}
                overlay={
                  <Popover id="familyId" style={{ pointerEvents: 'none' }}>
                    <Popover.Content>{familyIdValue}</Popover.Content>
                  </Popover>
                }
              >
                <div
                  ref={measuredFamilyIdRef}
                  style={{
                    width: `100%`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedText(familyIdValue || '', filter.term),
                  }}
                ></div>
              </OverlayTrigger>
            </div>
          );
        }),
        accessor: (row: any) => row.familyId,
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'disclosureId',
        Header: () => (
          <SortHeader
            text="case.disclosureId"
            onSortDownClick={() => updateDocumentSort('disclosureId', 'asc')}
            onSortUpClick={() => updateDocumentSort('disclosureId', 'desc')}
            sortMode={sortMode}
            columnId="disclosureId"
          />
        ),
        minWidth: '150px',
        width: `${columnsWidth.disclosureId}rem`,
        Cell: withResizeDetector(({ width, height, ...row }: any) => {
          const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
          const disclosureIdValue =
            row.disclosureId !== null && row.disclosureId !== undefined
              ? String(row.disclosureId)
              : '';

          const measuredDisclosureIdRef = useRef<HTMLDivElement>(null);

          useEffect(() => {
            setShouldShowTooltip(
              measuredDisclosureIdRef.current !== null &&
                measuredDisclosureIdRef.current.offsetWidth <
                  measuredDisclosureIdRef.current.scrollWidth,
            );
          }, [width, height]);

          return (
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%' }}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 500, hide: 400 }}
                trigger={shouldShowTooltip ? ['hover', 'focus'] : []}
                overlay={
                  <Popover id="disclosureId" style={{ pointerEvents: 'none' }}>
                    <Popover.Content>{disclosureIdValue}</Popover.Content>
                  </Popover>
                }
              >
                <div
                  ref={measuredDisclosureIdRef}
                  style={{
                    width: `100%`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedText(disclosureIdValue || '', filter.term),
                  }}
                ></div>
              </OverlayTrigger>
            </div>
          );
        }),
        accessor: (row: any) => row.disclosureId,
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => (
          <SortHeader
            text="case.author"
            onSortDownClick={() => updateDocumentSort('author', 'asc')}
            onSortUpClick={() => updateDocumentSort('author', 'desc')}
            sortMode={sortMode}
            columnId="author"
          />
        ),
        id: 'author',
        minWidth: isWiderColumns ? '190px' : '100px',
        width: `${columnsWidth.author}rem`,
        accessor: (row: any) => (
          <OverlayTrigger
            placement="top"
            delay={{ show: 500, hide: 400 }}
            trigger={row.author && row.author.length > 18 && ['hover', 'focus']}
            overlay={
              <Popover id="author" style={{ pointerEvents: 'none' }}>
                <Popover.Content>{row.author}</Popover.Content>
              </Popover>
            }
          >
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
              }}
            >
              {row.author}
            </div>
          </OverlayTrigger>
        ),
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => (
          <SortHeader
            text="case.recipient"
            onSortDownClick={() => updateDocumentSort('recipient', 'asc')}
            onSortUpClick={() => updateDocumentSort('recipient', 'desc')}
            sortMode={sortMode}
            columnId="recipient"
          />
        ),
        id: 'recipient',
        minWidth: isWiderColumns ? '190px' : '150px',
        width: `${columnsWidth.recipient}rem`,
        accessor: (row: any) => (
          <OverlayTrigger
            placement="top"
            delay={{ show: 500, hide: 400 }}
            trigger={row.recipientsList && row.recipientsList.length > 1 && ['hover', 'focus']}
            overlay={
              <Popover id="recipient" style={{ pointerEvents: 'none' }}>
                <Popover.Content>{row.recipientsList?.join(',')}</Popover.Content>
              </Popover>
            }
          >
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
              }}
            >
              {row.recipientsList?.join(',')}
            </div>
          </OverlayTrigger>
        ),
        onCustomSort: onCustomSortText,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'viewed',
        Header: () => (
          <SortHeader
            text="case.viewed"
            onSortDownClick={() => updateDocumentSort('viewed', 'asc')}
            onSortUpClick={() => updateDocumentSort('viewed', 'desc')}
            sortMode={sortMode}
            columnId="viewed"
          />
        ),
        accessor: (row: any) => row.viewed && moment(row.viewed).fromNow(),
        minWidth: '130px',
        width: `${columnsWidth.viewed}rem`,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'dateViewed',
        Header: () => (
          <SortHeader
            text={
              currentFolder && currentFolder.code === 'recently-viewed'
                ? 'case.dateViewed'
                : 'case.datePresented'
            }
            sortMode={false}
          />
        ),
        accessor: (row: any) => row.viewed && moment(row.viewed).format('DD-MM-YYYY HH:mm:ss'),
        minWidth: '130px',
        width: `${columnsWidth.dateViewed}rem`,
        onCustomSort: onCustomSortNum,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'docDate',
        Header: () => (
          <SortHeader
            text="case.docDate"
            onSortDownClick={() => updateDocumentSort('docDate', 'asc', onCustomSortDate)}
            onSortUpClick={() => updateDocumentSort('docDate', 'desc', onCustomSortDate)}
            sortMode={sortMode}
            columnId="docDate"
          />
        ),
        accessor: 'docDate',
        minWidth: isWiderDateColumn ? '150px' : '150px',
        width: `${columnsWidth.docDate}rem`,
        onCustomSort: onCustomSortDate,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'altDate',
        Header: () => (
          <SortHeader
            text="case.alternativeDate"
            onSortDownClick={() => updateDocumentSort('altDate', 'asc')}
            onSortUpClick={() => updateDocumentSort('altDate', 'desc')}
            sortMode={sortMode}
            columnId="altDate"
          />
        ),
        minWidth: '150px',
        width: `${columnsWidth.altDate}rem`,
        Cell: withResizeDetector(({ width, height, ...row }: any) => {
          const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

          const measuredAltDateRef = useRef<HTMLDivElement>(null);

          useEffect(() => {
            setShouldShowTooltip(
              measuredAltDateRef.current !== null &&
                measuredAltDateRef.current.offsetWidth < measuredAltDateRef.current.scrollWidth,
            );
          }, [width, height]);

          return (
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%' }}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 500, hide: 400 }}
                trigger={shouldShowTooltip ? ['hover', 'focus'] : []}
                overlay={
                  <Popover id="altDate" style={{ pointerEvents: 'none' }}>
                    <Popover.Content>{row.altDate}</Popover.Content>
                  </Popover>
                }
              >
                <div
                  ref={measuredAltDateRef}
                  style={{
                    width: `100%`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedText(row.altDate || '', filter.term),
                  }}
                ></div>
              </OverlayTrigger>
            </div>
          );
        }),
        accessor: (row: any) => row.altDate,
        onCustomSort: onCustomSortDate,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'dateUploaded',
        Header: () => (
          <SortHeader
            text="case.dateUploaded"
            onSortDownClick={() =>
              updateDocumentSort('dateUploaded', 'asc', (a: any, b: any, rowA: any, rowB: any) => {
                const dateA = rowA.createdBy?.date;
                const dateB = rowB.createdBy?.date;
                return onCustomSortUploadedDate(dateA, dateB);
              })
            }
            onSortUpClick={() =>
              updateDocumentSort('dateUploaded', 'desc', (a: any, b: any, rowA: any, rowB: any) => {
                const dateA = rowA.createdBy?.date;
                const dateB = rowB.createdBy?.date;
                return onCustomSortUploadedDate(dateA, dateB);
              })
            }
            sortMode={sortMode}
            columnId="dateUploaded"
          />
        ),
        accessor: (row: any) =>
          row.createdBy?.date ? moment(row.createdBy.date).format('DD/MM/YYYY HH:mm:ss') : '',
        minWidth: '250px',
        width: `${columnsWidth.dateUploaded}rem`,
        onCustomSort: (a: any, b: any, rowA: any, rowB: any) => {
          // Access the raw date from the row objects instead of the formatted display value
          const dateA = rowA.createdBy?.date;
          const dateB = rowB.createdBy?.date;
          return onCustomSortDate(dateA, dateB);
        },
        ...(sortMode && { sort: false }),
      },
      {
        id: 'classifyType',
        Header: () => (
          <SortHeader
            text="generic.classifyType"
            onSortDownClick={() =>
              updateDocumentSort('classifyType', 'asc', (a: any, b: any, rowA: any, rowB: any) => {
                const classifyTypeA = rowA.classify?.type || '';
                const classifyTypeB = rowB.classify?.type || '';
                return onCustomSortText(classifyTypeA, classifyTypeB);
              })
            }
            onSortUpClick={() =>
              updateDocumentSort('classifyType', 'desc', (a: any, b: any, rowA: any, rowB: any) => {
                const classifyTypeA = rowA.classify?.type || '';
                const classifyTypeB = rowB.classify?.type || '';
                return onCustomSortText(classifyTypeA, classifyTypeB);
              })
            }
            sortMode={sortMode}
            columnId="classifyType"
          />
        ),
        minWidth: '200px',
        width: `${columnsWidth.classifyType}rem`,
        accessor: (row: any) => row.classify?.type || '',
        onCustomSort: (a: any, b: any, rowA: any, rowB: any) => {
          // Access the raw classify.type from the row objects instead of the transformed accessor value
          const classifyTypeA = rowA.classify?.type || '';
          const classifyTypeB = rowB.classify?.type || '';
          return onCustomSortText(classifyTypeA, classifyTypeB);
        },
        ...(sortMode && { sort: false }),
      },
      {
        id: 'classifySubtype',
        Header: () => (
          <SortHeader
            text="generic.classifySubtype"
            onSortDownClick={() =>
              updateDocumentSort(
                'classifySubtype',
                'asc',
                (a: any, b: any, rowA: any, rowB: any) => {
                  const classifySubtypeA = rowA.classify?.subtype || '';
                  const classifySubtypeB = rowB.classify?.subtype || '';
                  return onCustomSortText(classifySubtypeA, classifySubtypeB);
                },
              )
            }
            onSortUpClick={() =>
              updateDocumentSort(
                'classifySubtype',
                'desc',
                (a: any, b: any, rowA: any, rowB: any) => {
                  const classifySubtypeA = rowA.classify?.subtype || '';
                  const classifySubtypeB = rowB.classify?.subtype || '';
                  return onCustomSortText(classifySubtypeA, classifySubtypeB);
                },
              )
            }
            sortMode={sortMode}
            columnId="classifySubtype"
          />
        ),
        minWidth: '230px',
        width: `${columnsWidth.classifySubtype}rem`,
        Cell: withResizeDetector(({ width, height, ...row }: any) => {
          const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
          const classifySubtypeValue = row.classify?.subtype || '';

          const measuredClassifySubtypeRef = useRef<HTMLDivElement>(null);

          useEffect(() => {
            setShouldShowTooltip(
              measuredClassifySubtypeRef.current !== null &&
                measuredClassifySubtypeRef.current.offsetWidth <
                  measuredClassifySubtypeRef.current.scrollWidth,
            );
          }, [width, height, classifySubtypeValue]);

          return (
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%' }}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 500, hide: 400 }}
                trigger={shouldShowTooltip ? ['hover', 'focus'] : []}
                overlay={
                  <Popover id="classifySubtype" style={{ pointerEvents: 'none' }}>
                    <Popover.Content>{classifySubtypeValue}</Popover.Content>
                  </Popover>
                }
              >
                <div
                  ref={measuredClassifySubtypeRef}
                  style={{
                    width: `100%`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedText(classifySubtypeValue, filter.term),
                  }}
                ></div>
              </OverlayTrigger>
            </div>
          );
        }),
        accessor: (row: any) => row.classify?.subtype || '',
        onCustomSort: (a: any, b: any, rowA: any, rowB: any) => {
          // Access the raw classify.subtype from the row objects instead of the transformed accessor value
          const classifySubtypeA = rowA.classify?.subtype || '';
          const classifySubtypeB = rowB.classify?.subtype || '';
          return onCustomSortText(classifySubtypeA, classifySubtypeB);
        },
        ...(sortMode && { sort: false }),
      },
      {
        id: 'type',
        Header: () => (
          <SortHeader
            text="case.type"
            onSortDownClick={() => updateDocumentSort('type', 'asc')}
            onSortUpClick={() => updateDocumentSort('type', 'desc')}
            sortMode={sortMode}
            columnId="type"
          />
        ),
        minWidth: '100px',
        width: `${columnsWidth.type}rem`,
        accessor: 'type',
        ...(sortMode && { sort: false }),
      },
      {
        id: 'pageCount',
        Header: () => (
          <SortHeader
            text="case.pageCount"
            onSortDownClick={() => updateDocumentSort('pageCount', 'asc')}
            onSortUpClick={() => updateDocumentSort('pageCount', 'desc')}
            sortMode={sortMode}
            columnId="pageCount"
          />
        ),
        minWidth: '90px',
        width: `${columnsWidth.pageCount}rem`,
        accessor: 'pageCount',
        onCustomSort: onCustomSortNum,
        ...(sortMode && { sort: false }),
      },
      {
        id: 'size',
        Header: () => (
          <SortHeader
            text="case.size"
            onSortDownClick={() => updateDocumentSort('size', 'asc', onCustomSortNum)}
            onSortUpClick={() => updateDocumentSort('size', 'desc', onCustomSortNum)}
            sortMode={sortMode}
            columnId="size"
          />
        ),
        minWidth: '120px',
        width: `${columnsWidth.size}rem`,
        accessor: (row: any) => (row.size ? bytesToMegaBytes(row.size) : '0 MB'),
        onCustomSort: onCustomSortNum,
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => <SortHeader text="case.info" sortMode={false} />,
        Cell: (row: any) => (
          <TableInfo
            row={row}
            customColumns={customColumns ? customColumns : []}
            filter={filter.term}
            present={present}
            isPresentModePage={isPresentModePage}
            handleShow={handleShow}
            getFolderSpecificFileDetails={getFolderSpecificFileDetails}
            onRowSelect={onRowSelect}
            onOpenViewDrawer={onOpenViewDrawer}
          />
        ),
        id: 'info',
        minWidth: '190px',
        width: `${columnsWidth.info}rem`,
        onCustomSort: (
          a: any,
          b: any,
          {
            duplicate,
            source,
            hasPublicHyperlinks,
            hasAnnotations,
            isPrivate,
            notes,
            showMoreInfoIcon,
            tags,
            attachments,
            locations,
          }: any,
          {
            duplicate: duplicateB,
            source: sourceB,
            hasPublicHyperlinks: hasPublicHyperlinksB,
            hasAnnotations: hasAnnotationsB,
            isPrivate: isPrivateB,
            notes: notesB,
            showMoreInfoIcon: showMoreInfoIconB,
            tags: tagsB,
            attachments: attachmentsB,
            locations: locationsB,
          }: any,
        ) => {
          let comparison = 0;
          if (duplicate) ++comparison;
          if (source) ++comparison;
          if (hasPublicHyperlinks) ++comparison;
          if (hasAnnotations) ++comparison;
          if (isPrivate) ++comparison;
          if (notes) ++comparison;
          if (showMoreInfoIcon) ++comparison;
          if (tags && tags.length > 0) ++comparison;
          if (attachments && attachments.length > 0) ++comparison;
          if (locations && locations.length > 0) ++comparison;
          if (duplicateB) --comparison;
          if (sourceB) --comparison;
          if (hasPublicHyperlinksB) --comparison;
          if (hasAnnotationsB) --comparison;
          if (isPrivateB) --comparison;
          if (notesB) --comparison;
          if (showMoreInfoIconB) --comparison;
          if (tagsB && tagsB.length > 0) --comparison;
          if (attachmentsB && attachmentsB.length > 0) --comparison;
          if (locationsB && locationsB.length > 0) --comparison;
          return comparison;
        },
        ...(sortMode && { sort: false }),
      },
      {
        Header: () => null,
        Cell: (row: any) => (
          <TableActions
            row={row}
            handleShow={handleShow}
            getFolderSpecificFileDetails={getFolderSpecificFileDetails}
            onRowSelect={onRowSelect}
            onOpenViewDrawer={onOpenViewDrawer}
          />
        ),
        id: 'actions',
        stickyRight: true,
        minWidth: '60px',
        width: `${columnsWidth.actions}rem`,
        sort: false,
      },
    ]
      .filter((col) => columns.includes(col.id))
      .map((col) => {
        if (['index', 'actions'].includes(col.id)) {
          return col;
        }
        const OriginalHeader = col.Header;
        return {
          ...col,
          Header: () => (
            <ColumnHeaderMenu columnId={col.id} sortMode={sortMode} isSmallView={Boolean(file)}>
              {typeof OriginalHeader === 'function' ? OriginalHeader() : OriginalHeader}
            </ColumnHeaderMenu>
          ),
        };
      });
  },
);
